import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useDocumentTitle from "../../utils/useDocumentTitle";
import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import BackButton from "../../components/BackButton";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { useNotification } from "../../context/notification";
import PromptTemplateService from "../../services/PromptTemplateService";
import { getLocalValue } from "../../utils/auth";
import PromptTemplateForm from "../../components/promptTemplate/PromptTemplateForm";

const INITIAL_VALUES = {
  name: "",
  template_type: "",
  prompt_content: "",
  description: "",
  is_active: 1,
  created_by: null,
  updated_by: null,
};

export function PromptTemplateFormPage(props) {
  useDocumentTitle(props.title);
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const { addNotification } = useNotification();
  const isEditMode = Boolean(id);

  const [initialValues, setInitialValues] = useState(INITIAL_VALUES);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditMode);

  const fetchTemplate = async () => {
    setIsFetching(true);
    try {
      const response = await PromptTemplateService.getById(id);
      setInitialValues(response.data);
    } catch (error) {
      addNotification("Failed to load template", "error");
      history.push("/prompt-templates");
    }
    setIsFetching(false);
  };

  useEffect(() => {
    if (isEditMode) {
      fetchTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = async values => {
    setIsLoading(true);
    try {
      const payload = {
        ...values,
        updated_by: parseInt(getLocalValue("uid")),
      };

      if (isEditMode) {
        await PromptTemplateService.update(id, payload);
        addNotification("Template updated successfully", "success");
      } else {
        payload.created_by = parseInt(getLocalValue("uid"));
        await PromptTemplateService.create(payload);
        addNotification("Template created successfully", "success");
      }
      history.push("/prompt-templates");
    } catch (error) {
      const errorMessage = error.response?.data?.error || "An error occurred";
      addNotification(errorMessage, "error");
    }
    setIsLoading(false);
  };

  const handleCancel = () => {
    history.push("/prompt-templates");
  };

  if (isFetching) {
    return (
      <DashboardLayout isDisableCampaignSelection={true}>
        <PageContent>
          <Section>
            <div>Loading...</div>
          </Section>
        </PageContent>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout isDisableCampaignSelection={true}>
      <PageContent>
        <Section>
          <div style={{ marginBottom: "16px" }}>
            <BackButton />
          </div>
          <SectionTitle>
            {isEditMode
              ? t("promptTemplate.editTitle") || "Edit Prompt Template"
              : t("promptTemplate.createTitle") || "Create Prompt Template"}
          </SectionTitle>
          <PromptTemplateForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            onCancel={handleCancel}
          />
        </Section>
      </PageContent>
    </DashboardLayout>
  );
}